import { INIT_DATA_STORAGE_KEY } from '@/app/constants'
import { UserData } from '@/app/types'
import { useEffect, useState } from 'react'

interface UseUserDataReturn {
	userData: UserData | null
	isLoading: boolean
	error: Error | null
}

/**
 * Custom hook to safely load user data from localStorage with error handling
 * Handles cases like private browsing mode, corrupted data, and localStorage access failures
 */
export const useUserData = (): UseUserDataReturn => {
	const [userData, setUserData] = useState<UserData | null>(null)
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState<Error | null>(null)

	useEffect(() => {
		const loadUserData = () => {
			try {
				// Check if localStorage is available
				if (typeof window === 'undefined' || !window.localStorage) {
					throw new Error('localStorage is not available')
				}

				const storedData = localStorage.getItem(INIT_DATA_STORAGE_KEY)

				if (!storedData) {
					// No user data yet, this is not an error
					setUserData(null)
					setIsLoading(false)
					return
				}

				// Try to parse the stored data
				const parsedData = JSON.parse(storedData) as UserData

				// Validate required fields
				if (!parsedData.userCountry || !parsedData.defaultCurrency) {
					throw new Error('Invalid user data structure')
				}

				setUserData(parsedData)
				setError(null)
			} catch (err) {
				console.error('Error loading user data:', err)

				// Set error but don't crash the app
				setError(
					err instanceof Error ? err : new Error('Failed to load user data')
				)

				// Provide minimal fallback data to keep the app functional
				const fallbackData: UserData = {
					userCountry: 'US',
					defaultCurrency: {
						label: 'US Dollar',
						flag: 'USA',
						abbreviation: 'USD',
					},
					email: '',
					userIP: '127.0.0.1',
					userAgent: navigator.userAgent || 'Mozilla/5.0',
				}

				setUserData(fallbackData)
			} finally {
				setIsLoading(false)
			}
		}

		loadUserData()

		// Listen for storage events (changes from other tabs)
		const handleStorageChange = (e: StorageEvent) => {
			if (e.key === INIT_DATA_STORAGE_KEY) {
				loadUserData()
			}
		}

		window.addEventListener('storage', handleStorageChange)

		return () => {
			window.removeEventListener('storage', handleStorageChange)
		}
	}, [])

	return { userData, isLoading, error }
}
